// SupabaseStore — reads + realtime subs + write-through dispatch.
//
// Optimistic-UI loop:
//   1. Reducer computes the next state from the action; we apply locally
//      and notify listeners immediately (UI feels snappy).
//   2. applyAction translates the action into one or more Supabase writes.
//   3. On failure (RLS denial, network), we re-fetch authoritative state.
//   4. Realtime echoes from other clients also trigger re-fetch — they win
//      over our optimistic guess if there's a divergence.
//
// Edge Function actions (advance/swap/reschedule/forfeit) are still stubbed —
// see supabase-actions.ts. They run locally, then the next realtime tick or
// reload will revert. Phase 4 implements them properly.
//
// Lifecycle:
// - Construction kicks off an initial fetch of every table we read from.
// - Each table gets its own postgres_changes channel; any change triggers a
//   full re-fetch of the affected slice. Coarse but correct; we'll narrow it
//   down to per-row reconciliation when write churn becomes noticeable.
// - Initial state is mockInitialState — it gets replaced by the first fetch's
//   result. Brief flash of mock data on first load is acceptable for v1.
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Action, AppRole, AppState } from '../types';
import { initialState as mockInitialState, reducer } from '../state/reducer';
import type { RealtimeStatus, Store, SystemStatus } from './Store';
import { applyAction, readAuthContext } from './supabase-actions';
import { SUPERUSER_EMAILS } from '../lib/auth';
import { APP_SCHEMA_VERSION } from '../lib/version';
import { createCoalescedRunner } from './coalesce';
import { assembleState } from './assembleState';
import { fetchRawReads } from './fetchRawReads';

// Access Control actions reconcile against the (superuser-only) roster + the
// volunteer allow-list, which live outside the realtime get_app_state path.
const ACCESS_ACTIONS = new Set(['SET_USER_ACCESS', 'ADD_VOLUNTEER_EMAIL', 'REMOVE_VOLUNTEER_EMAIL']);

export function createSupabaseStore(supabase: SupabaseClient): Store {
  // Start from the mock shape but blank the Access Control slices — those are
  // fetched separately and only for superusers (the mock seed must not leak).
  let state: AppState = { ...mockInitialState, accessRoster: [], volunteerEmails: [] };
  const listeners = new Set<() => void>();
  const notify = () => listeners.forEach((l) => l());

  let systemStatus: SystemStatus = {
    realtime: 'connecting',
    schemaVersionMismatch: null,
    lastError: null,
  };
  const updateStatus = (patch: Partial<SystemStatus>) => {
    systemStatus = { ...systemStatus, ...patch };
    notify();
  };

  const fetchAll = async () => {
    const t0 = (typeof performance !== 'undefined') ? performance.now() : Date.now();
    const result = await fetchRawReads(supabase);
    if (!result.ok) {
      // eslint-disable-next-line no-console
      console.warn(`[SupabaseStore] fetchAll: core read(s) failed (${result.failed.join(', ')}); keeping last-good state`);
      updateStatus({ lastError: { actionType: 'fetchAll', message: `core read failed: ${result.failed.join(', ')}`, at: Date.now() } });
      return;
    }

    // Shared transform — see assembleState.ts. fetchRawReads + assembleState are
    // exactly what the public edge-cache snapshot runs (with the anon key).
    const slices = assembleState(result.raw);
    state = { ...state, ...slices };

    // Schema version cross-check. Surfaces as a "Reload required" banner.
    const serverVersion = (result.raw.eventState as { app_schema_version?: number } | null)?.app_schema_version;
    if (typeof serverVersion === 'number' && serverVersion !== APP_SCHEMA_VERSION) {
      if (systemStatus.schemaVersionMismatch?.server !== serverVersion) {
        updateStatus({ schemaVersionMismatch: { client: APP_SCHEMA_VERSION, server: serverVersion } });
      }
    } else if (systemStatus.schemaVersionMismatch) {
      updateStatus({ schemaVersionMismatch: null });
    }

    notify();
    const elapsed = ((typeof performance !== 'undefined') ? performance.now() : Date.now()) - t0;
    // eslint-disable-next-line no-console
    console.debug(`[SupabaseStore] fetchAll ${elapsed.toFixed(0)}ms (3v3=${slices.matches3v3.length}, 1v1=${slices.matches1v1.length}, queue=${slices.queue.length})`);
  };

  // Access Control roster + volunteer allow-list. get_access_roster is
  // superuser-only and RAISES insufficient_privilege for anyone else. Gate the
  // call on the (client-mirrored) superuser list so non-superusers don't fire a
  // guaranteed-to-fail RPC — that failure was harmless (caught below) but logged
  // a 42501 Postgres ERROR on every non-superuser sign-in/reconnect, drowning the
  // error log. The server still re-checks; this only skips the doomed round-trip.
  // Not part of the realtime path; reconciled explicitly after access actions.
  const fetchAccessControl = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const email = session?.user.email?.toLowerCase() ?? null;
    if (!email || !SUPERUSER_EMAILS.includes(email)) return;  // non-superuser → roster stays empty
    try {
      const { data: roster, error: rErr } = await supabase.rpc('get_access_roster');
      if (rErr) throw rErr;
      const { data: vols } = await supabase.from('volunteer_emails').select('email');
      const volSet = new Set((vols ?? []).map((v: { email: string }) => v.email.toLowerCase()));
      // Fallback effective role if the server didn't supply one (e.g. migration
      // 87 not yet applied) — best-effort from override / volunteer-list /
      // @arc-robotics.org domain, so the roster never silently blanks. ops_emails
      // isn't client-readable, so ops may show as staff until 87 lands.
      const deriveEffective = (email: string, override: AppRole | null): AppRole | null =>
        override
        ?? (volSet.has(email.toLowerCase()) ? 'volunteer'
          : email.toLowerCase().endsWith('@arc-robotics.org') ? 'staff' : null);
      state = {
        ...state,
        accessRoster: (roster ?? []).map((r: Record<string, unknown>) => {
          const override = (r.role_override as AppRole | null) ?? null;
          const email = r.email as string;
          return {
            userId: r.user_id as string,
            email,
            role: override,
            effectiveRole: (r.effective_role as AppRole | null) ?? deriveEffective(email, override),
            l3Override: r.l3_override === true,
            captainsMeeting: r.captains_meeting === true,
            lastSeenAt: (r.last_seen_at as string | null) ?? null,
          };
        }),
        volunteerEmails: (vols ?? []).map((v: { email: string }) => v.email).sort(),
      };
      notify();
    } catch {
      // Non-superuser (insufficient_privilege) or transient — leave empty.
    }
  };

  // All refetch triggers (realtime events, reconnect, write-failure reverts)
  // funnel through one coalescing runner: a burst of per-table change events
  // collapses into a single refetch, and single-flight means refetches can't
  // overlap or apply out of order. See coalesce.ts.
  const refetch = createCoalescedRunner(fetchAll, { debounceMs: 250 });

  // Initial load — run immediately, bypassing the debounce.
  void refetch.runNow();
  // Self-register the signed-in user (populates user_profiles) + load the
  // Access Control data for superusers. Both fire-and-forget.
  void supabase.rpc('touch_my_profile').then(() => fetchAccessControl());

  // Realtime: each table gets a channel. Any change triggers a full slice
  // refetch. Crude but correct; refine to per-row reconciliation later.
  // Channel names include a per-store UUID so React 18 strict mode (which
  // double-invokes useMemo factories in dev) doesn't try to re-attach a
  // listener to an already-subscribed channel.
  const storeId = (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);
  const tables = [
    'matches', 'match_state', 'event_state', 'inspection_queue',
    'warnings', 'check_in', 'field_calibration', 'team_pictures', 'robot_inspection_station', 'pre_inspection_level',
    'match_rounds', 'match_notes', 'team_notes',
    // Referee System (migration 95): borrowed-module counts + change log, so a
    // second ops client sees checkouts/returns live.
    'referee_modules', 'referee_module_log',
    // Per-instance robot weights (migration 72). Subscribe so a weight
    // entered at the inspection desk shows live on Match Control.
    'robot_weight',
    // Captain's Meeting commits (go_live_quals_*) write to teams.
    // Subscribe so other clients see the new groups/seeds live.
    'teams',
  ];
  // ONE channel carries every table subscription (multiple postgres_changes
  // bindings on a single channel) instead of 15 separate channels — 15× fewer
  // channels on the Realtime server (less per-channel memory + quota headroom),
  // which matters most under event-load write churn.
  const channel = supabase.channel(`store-${storeId}`);
  for (const table of tables) {
    channel.on('postgres_changes', { event: '*', schema: 'public', table }, () => {
      // Coalesced: a single action touches several tables, so this fires many
      // times in a burst — the runner collapses them into one refetch.
      refetch.schedule();
    });
  }
  channel.subscribe((status) => {
    // The single channel's status now IS the realtime health.
    const next: RealtimeStatus =
      status === 'SUBSCRIBED' ? 'connected'
        : (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') ? 'degraded'
          : 'connecting';
    if (next !== systemStatus.realtime) updateStatus({ realtime: next });
  });

  // Browser-level connectivity hooks for fast offline detection. The channel
  // subscribe() callback only flips to TIMED_OUT after ~60s; the browser knows
  // immediately when it loses the network, so we mirror that into systemStatus
  // right away. On reconnect we poke a refetch and let the channel report its
  // own SUBSCRIBED state.
  let removeConnectivityHooks = () => {};
  if (typeof window !== 'undefined') {
    const onOffline = () => {
      if (systemStatus.realtime !== 'degraded') updateStatus({ realtime: 'degraded' });
    };
    const onOnline = () => { refetch.schedule(); };
    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);
    if (!navigator.onLine) onOffline();
    removeConnectivityHooks = () => {
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
    };
  }

  return {
    kind: 'supabase',
    getState: () => state,
    dispatch: (action: Action) => {
      // Optimistic: apply reducer locally first, then sync to DB asynchronously.
      const prev = state;
      const next = reducer(state, action);
      if (next !== state) {
        state = next;
        notify();
      }
      // Fire-and-forget DB sync. On any failure, re-fetch authoritative state
      // so the UI converges to truth.
      void (async () => {
        try {
          const auth = await readAuthContext(supabase);
          await applyAction(supabase, action, prev, next, auth);
          // Access Control writes aren't in the realtime path — pull authoritative
          // roster/allow-list so the optimistic edit converges to truth.
          if (ACCESS_ACTIONS.has(action.type)) await fetchAccessControl();
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          // eslint-disable-next-line no-console
          console.error(`[SupabaseStore] action ${action.type} failed; reverting to DB state:`, err);
          updateStatus({ lastError: { actionType: action.type, message, at: Date.now() } });
          if (ACCESS_ACTIONS.has(action.type)) await fetchAccessControl();
          else refetch.schedule();
        }
      })();
    },
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSystemStatus: () => systemStatus,
    clearLastError: () => {
      if (systemStatus.lastError) updateStatus({ lastError: null });
    },
    // Tear down on store swap (e.g. a vol+ signs out → public snapshot store):
    // drop the realtime channel, the connectivity listeners, and the refetch
    // timer so nothing leaks. (Previously the per-table channels were never
    // removed.)
    dispose: () => {
      void supabase.removeChannel(channel);
      removeConnectivityHooks();
      refetch.dispose();
    },
  };
}
